import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createApiToken,
  listApiTokens,
  revokeApiToken,
  type ApiTokenCreated,
} from "@/api/apiTokens";
import { useToast } from "@/components/toast";

const QUERY_KEY = ["admin-api-tokens"];

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("ru-RU");
}

export default function AdminApiTokens() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [created, setCreated] = useState<ApiTokenCreated | null>(null);

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const r = await listApiTokens();
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (tokenName: string) => {
      const r = await createApiToken(tokenName);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: (data) => {
      setCreated(data);
      setName("");
      toast(`Токен «${data.name}» создан`, "success");
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => toast(`Не создалось: ${err.message}`, "error"),
  });

  const revokeMutation = useMutation({
    mutationFn: async (tokenId: string) => {
      const r = await revokeApiToken(tokenId);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast("Токен отозван", "success");
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => toast(`Не отозвалось: ${err.message}`, "error"),
  });

  const tokens = query.data?.tokens ?? [];

  return (
    <div style={{ padding: "var(--sp-3)", maxWidth: 1000 }}>
      <header style={{ marginBottom: "var(--sp-3)" }}>
        <h1>API-токены</h1>
        <p className="mt-text-tight mt-muted">
          Bearer-токены для machine-to-machine доступа к{" "}
          <code>POST /v1/external/translate</code>. Plain-значение токена
          показывается только один раз при создании — сохрани сразу,
          восстановить нельзя, можно только отозвать и выпустить новый.
        </p>
      </header>

      <section className="rf-section" style={{ marginBottom: "var(--sp-3)" }}>
        <div className="rf-section__head">
          <span>Создать токен</span>
        </div>
        <div className="rf-section__body">
          <form
            style={{ display: "flex", gap: 8, alignItems: "center" }}
            onSubmit={(ev) => {
              ev.preventDefault();
              const trimmed = name.trim();
              if (trimmed) createMutation.mutate(trimmed);
            }}
          >
            <input
              className="mt-input"
              style={{ flex: 1 }}
              placeholder="имя клиента (например: aknaucer-prod)"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              maxLength={64}
            />
            <button
              type="submit"
              className="mt-btn mt-btn--primary"
              disabled={createMutation.isPending || !name.trim()}
            >
              Создать
            </button>
          </form>

          {created && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                background: "#fff8e1",
                border: "1px solid #f0d678",
                borderRadius: 8,
              }}
            >
              <strong>Токен «{created.name}» создан.</strong>
              <code
                style={{
                  display: "block",
                  marginTop: 8,
                  padding: 8,
                  background: "#fff",
                  border: "1px solid var(--border-default, #d0d5dd)",
                  borderRadius: 4,
                  wordBreak: "break-all",
                  fontSize: 13,
                }}
              >
                {created.token}
              </code>
              <div
                className="mt-text-tight mt-muted"
                style={{ marginTop: 8, display: "flex", gap: 8 }}
              >
                <button
                  type="button"
                  className="mt-btn mt-btn--secondary mt-btn--sm"
                  onClick={() => {
                    void navigator.clipboard.writeText(created.token);
                    toast("Скопировано", "success");
                  }}
                >
                  Скопировать
                </button>
                <button
                  type="button"
                  className="mt-btn mt-btn--ghost mt-btn--sm"
                  onClick={() => setCreated(null)}
                >
                  Скрыть
                </button>
                <span style={{ alignSelf: "center" }}>
                  После закрытия восстановить значение будет нельзя.
                </span>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="rf-section">
        <div className="rf-section__head">
          <span>Существующие токены</span>
          <span className="mt-toolbar__sep" style={{ flex: 1 }} />
          <span className="mt-text-tight mt-muted">api_tokens</span>
        </div>
        <div className="rf-section__body">
          {query.isLoading && <div className="mt-empty">Загружаем…</div>}
          {query.isError && (
            <div className="mt-empty mt-empty--error">
              {(query.error as Error | undefined)?.message ?? "Ошибка"}
            </div>
          )}
          {!query.isLoading && !query.isError && tokens.length === 0 && (
            <div className="mt-empty">Токенов пока нет.</div>
          )}
          {tokens.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Имя", "Создан", "Создал", "Последнее использование", "Статус", ""].map(
                    (h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: "left",
                          padding: "8px 10px",
                          borderBottom: "1px solid var(--border-default, #d0d5dd)",
                          fontSize: 12,
                        }}
                        className="mt-muted"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.token_id}>
                    <td style={cellStyle}>
                      <strong>{t.name}</strong>
                    </td>
                    <td style={cellStyle}>{fmtDate(t.created_at)}</td>
                    <td style={cellStyle}>{t.created_by}</td>
                    <td style={cellStyle}>{fmtDate(t.last_used_at)}</td>
                    <td style={cellStyle}>
                      <span
                        className={
                          "mt-badge mt-badge--no-dot " +
                          (t.status === "active"
                            ? "mt-badge--success"
                            : "mt-badge--neutral")
                        }
                      >
                        {t.status === "active" ? "активен" : "отозван"}
                      </span>
                    </td>
                    <td style={cellStyle}>
                      {t.status === "active" && (
                        <button
                          type="button"
                          className="mt-btn mt-btn--danger mt-btn--sm"
                          disabled={revokeMutation.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Отозвать токен «${t.name}»? Действие необратимо.`,
                              )
                            ) {
                              revokeMutation.mutate(t.token_id);
                            }
                          }}
                        >
                          Отозвать
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

const cellStyle = {
  padding: "8px 10px",
  borderBottom: "1px solid var(--border-subtle, #e4e7ec)",
} as const;
